import { useRefresh } from 'react-admin';
import DeleteIcon from '@mui/icons-material/Delete';
import CallMissedIcon from '@mui/icons-material/CallMissed';
import DoneIcon from '@mui/icons-material/Done';
import {formatCurrency, request, convertMessageCodeToMessage, downloadFileFromResponse, getRequestOptions, notifyConfirm, notifyError, notifySuccess} from "../../util";

import {translate} from "../../i18n/customI18nProvider";
import config from '../../config';
import Button  from '@material-ui/core/Button';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';

import Swal from 'sweetalert2';
import { TableRow } from '@material-ui/core';
import { Card, CardContent, Fade, IconButton, Modal, Stack, Table, TableBody, TableCell, TableHead } from '@mui/material';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import { executeRefund, refundTransaction } from '../../services/client.service';
import { Call, Close, Visibility } from '@mui/icons-material';
import { useState } from 'react';
import TransactionModal from './TransactionModal';
import ResponsiveTable from '../../components/table/ResponsiveTable';
import { SWIPE_ANIMATIONS } from '../../components/table/SwipeableRow';

const styles = {
  modal: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  paper: {
    backgroundColor: 'whitesmoke',
    borderRadius: '10px',
    padding: '20px',
    minWidth: '40vw'
  },
};

const getTransactionSubtype = (record) => {
    let subtype = null;
    if(record.payment_type) {
        subtype = translate('custom.by') + ' ' + translate('custom.' + record.payment_type);
    } else if(record.forced) {
        subtype = translate('custom.forced');
    } else if(record.order_number) {
        subtype = record.order_number;
    } else if((record.type == config.transaction_types.ENTRANCE || record.type == config.transaction_types.ZONE) && record?.order?.[0]?.name){
        subtype = record?.order?.[0]?.name;
    } else if(record.type == config.transaction_types.CHECKOUT) {
        subtype = translate('custom.by') + ' ' + translate('custom.cash');
    } else if([config.transaction_types.MYPOS_REFUND, config.transaction_types.CARD_REFUND, config.transaction_types.TWINT_REFUND].includes(record.type)){
        subtype = translate('custom.auto_refund');
    }

    let detail = null;
    if(record.payment_type === config.payment_types.POS_PHONE.value) {
        let pan = record.pan;
        if(pan) {
            detail = pan;
        }
        if(pan.length == 5) {
            detail = '#' + pan.substring(1);
        }

        subtype += ' ' + detail;
    }
    return subtype;
}


const TransactionTypeView = ({record}) => {
    const subtype = getTransactionSubtype(record);
    return (
        <>
            {translate('custom.' + record.type)}
            <br />
            {subtype ? (<small style={{color: 'gray'}}>{subtype}</small>) : null} 
        </>
    )
}

const TransactionIdView = ({record}) => {
    return (
        <>
            {record?.referenceNumber ? (record.referenceNumber) : null}
            {record?.twint_direct_merchant_tran_id ? (record.twint_direct_merchant_tran_id) : null}
            {(!record?.card_transaction_id && !record?.twint_direct_merchant_tran_id && record?.transaction_uuid) ? (record.transaction_uuid) : null}
        </>
    )
}

const getRefundMethod = (record) => {
    let method = null;
    if(record.payment_type === config.payment_types.MYPOS.value || record.payment_type === config.payment_types.POS_PHONE.value) method = config.refund_methods.MYPOS;
    if(record.payment_type === config.payment_types.CARD.value) method = config.refund_methods.CARD;
    return method;
}

const refundable_methods = [
    config.payment_types.TWINT.value,
    config.payment_types.TWINT_DIRECT.value,
    config.payment_types.MYPOS.value,
    config.payment_types.CARD.value,
    config.payment_types.POS_PHONE.value
]


const TwintRefundView = ({record}) => {

    const refresh = useRefresh();
    if(record.type === 'credit' && refundable_methods.includes(record.payment_type)) {
        const method = getRefundMethod(record);
        if(record.full_refund) {
            return (
                <div style={{textAlign: 'center'}}>
                    <DoneIcon />
                </div>
            )
        } else {
            return (
                <div style={{textAlign: 'center'}}>
                    <CallMissedIcon style={{'cursor' : 'pointer'}}
                        onClick={async () => {
                            await refundTransaction(record.client_uid, record._id, method)
                            refresh();
                        }}
                    />
                </div>
            )
        }
    } else {
        return (
            <></>
        )
    }
}

const downloadClientTransactions = async (uid, mode='xlsx') => {
    let response = await fetch(config.apiUrl + '/clients/' + uid + '/transactions/export/' + mode, getRequestOptions('GET'));
    if(response.status !== 200) {
        let responseJson = await response.json();
        Swal.fire({
            title: translate('custom.error'),
            text: responseJson.error_code ? convertMessageCodeToMessage(responseJson.error_code) : responseJson.error,
            icon: 'error',
            confirmButtonText: translate('custom.ok'),
            confirmButtonColor: config.baseColor
        });
        return;
    } else {
        await downloadFileFromResponse(response, translate('custom.client') + '_' + uid + '.' + mode, false);
    }
}

const AmountView = (props) => {
    const { record } = props;
    return (
        <div style={{textAlign: 'right', color: record.type === 'credit' ? config.successColor : config.dangerColor}}>
            { props.currency + ' ' + formatCurrency(record.amount)}
        </div>
    )
}

const AmountRefundableView = ({record, currency}) => {
    
    const refundable_methods = [
        config.payment_types.TWINT.value,
        config.payment_types.TWINT_DIRECT.value,
        config.payment_types.MYPOS.value,
        config.payment_types.CARD.value,
        config.payment_types.POS_PHONE.value
    ]
    if(record.type === 'credit' && refundable_methods.includes(record.payment_type)) {
        let refundable_amount = record.amount;
        if(record.refund_amount) {
            refundable_amount -= record.refund_amount;
        }
        if(record.full_refund) {
            refundable_amount = 0;
        }
        return (
            <div style={{textAlign: 'right'}}>
                {currency} {formatCurrency(refundable_amount)}
            </div>
        )
    } else {
        return (
            <></>
        )
    }
}

async function executeDeleteTransaction(client_uid, transaction_id, force=false) {
    let url = '/clients/transaction/' + client_uid + '/' + transaction_id;
    if (force) url += '?force=true';
    let transaction = await request(url, 'DELETE', null);
    return transaction;
}

async function deleteTransaction(client_uid, transaction_id, force=false) {
    let confirmationMessage = 'custom.do_you_want_to_delete_transaction';
    if(force) {
        confirmationMessage = 'custom.do_you_want_to_delete_this_and_linked_transaction';
    }
    const confirmation = await notifyConfirm(confirmationMessage);

    if(!confirmation.isConfirmed) return;

    const transaction = await executeDeleteTransaction(client_uid, transaction_id, force);
    if (transaction.error) {
        let error_message = transaction?.error_code ? convertMessageCodeToMessage(transaction.error_code) : transaction.error;
        notifyError(error_message)
        return;
    }
    notifySuccess('custom.transaction_deleted_successfully');
}

const DeleteTransactionView = (props) => {

    const { record } = props;
    const client_uid = record["client_uid"];
    const id = record["_id"]
    const refresh = useRefresh();

    return (
        <>
            {/* If 'payment_type' is 'wristband', then force delete ( with linked transaction ) */}
            {((record.payment_type === 'wristband') && ((record.type == config.transaction_types.CREDIT) || (record.type == config.transaction_types.BALANCE_TRANSFER_FROM_WRISTBAND))) ? (
                <DeleteSweepIcon style={{'cursor': 'pointer', color: config.dangerColor, marginLeft: 5}}
                    onClick={async () => {
                        await deleteTransaction(client_uid, id, true)
                        refresh();
                    }}
                />
            ) : (
                <DeleteIcon style={{'cursor': 'pointer', color: config.dangerColor}}
                    onClick={async () => {
                        await deleteTransaction(client_uid, id)
                        refresh();
                    }}
                />
            )}
        </>
    )
}

export const TransactionsList = (props) => {
    const record = props.record;
    const isSmall = props.isSmall ? props.isSmall : false;
    const currency = props.currency;
    const refresh = useRefresh();

    const [transactionModal, setTransactionModal] = useState({open: false, transaction: null});

    const ShowTransactionView = (props) => {    
        const { record } = props;
        return (
            <Visibility style={{'cursor': 'pointer', color: config.baseColor, marginLeft: 5, marginRight: 20}}
                onClick={async () => {
                    setTransactionModal({open: true, transaction: record});
                }}
            />
        )
    }

    const handleSwipeDelete = async (t) => {
        const response = await executeDeleteTransaction(t.client_uid, t._id);
        if (response.error) {
            let error_message = response?.error_code ? convertMessageCodeToMessage(response.error_code) : response.error;
            notifyError(error_message);
        } else {
            notifySuccess('custom.transaction_deleted_successfully');
        }
        refresh();
    }

    const handleSwipeRefund = async (t) => {
        await executeRefund(t.client_uid, t._id, null);
        refresh();
    }

    return (
        <>
            <Grid container spacing={2}>

                {/* hr */}
                <Grid item xs={12}>
                    <hr />
                </Grid>

                <Grid item xs={12}>
                    <Stack direction="row" spacing={2} style={{justifyContent: 'space-between'}}>
                        <Typography variant="h6">{translate('custom.transactions')}</Typography>
                        <Grid>
                            <Button
                                onClick={() => {
                                    downloadClientTransactions(record.uid, 'xlsx')
                                }}
                                variant="outlined"
                                style={{borderColor: config.baseColor, color: config.baseColor, marginRight: 2}}
                            >
                                {(isSmall ? '' : translate('custom.export') + ' - ') + translate('custom.xlsx')}
                            </Button>
                            <Button
                                onClick={() => {
                                    downloadClientTransactions(record.uid, 'pdf')
                                }}
                                variant="outlined"
                                style={{borderColor: config.baseColor, color: config.baseColor}}
                                sx={{marginRight: 2}}
                            >
                                {(isSmall ? '' : translate('custom.export') + ' - ') + translate('custom.pdf')}
                            </Button>
                        </Grid>
                    </Stack>
                </Grid>

                <Grid item xs={12}>
                    <ResponsiveTable size='small' sx={{width: '100%', overflowX: 'scroll'}} data={record?.transactions.map((t) => ({...t, client_uid: record.uid}))} keyField='_id'
                        mobile={{
                            title: t => new Date(t.datetime).toLocaleString(),
                            subtitle: t => (translate('custom.' + t.type) + (getTransactionSubtype(t) ? ' - ' + getTransactionSubtype(t) : '')),
                            right: t => 
                                <AmountView
                                    currency={currency}
                                    record={t}
                                />,
                            lines: [
                                t => (<TransactionIdView record={t} />),
                            ],
                        }}
                        onClick={async (t) => {setTransactionModal({open: true, transaction: t});}}
                        
                        swipeAction={{
                            icon: <DeleteIcon color='error' />,
                            onClick: (record) => handleSwipeDelete(record),
                            label: translate('custom.delete'),
                            animation: SWIPE_ANIMATIONS.remove,
                            confirmation: {confirm: true, message: translate('custom.do_you_want_to_delete_transaction')}
                        }}

                        secondarySwipeAction={{
                            icon: <CallMissedIcon color='warning' />,
                            onClick: (record) => handleSwipeRefund(record),
                            label: translate('custom.refund'),
                            animation: SWIPE_ANIMATIONS.loading,
                            confirmation: {confirm: true, message: translate('custom.do_you_want_to_refund_transaction')},
                            condition: (record) => (record.type === 'credit' && refundable_methods.includes(record.payment_type) && !record.refunded)
                        }}
                    >
                        <TableHead>
                            <TableRow>
                                <TableCell>{translate('custom.date')}</TableCell>
                                <TableCell>{translate('custom.type')}</TableCell>
                                <TableCell>{translate('custom.transaction_id')}</TableCell>
                                <TableCell align='right'>{translate('custom.amount')}</TableCell>
                                <TableCell align='right'>{translate('custom.amount_refundable')}</TableCell>
                                <TableCell align='center'>{translate('custom.refund')}</TableCell>
                                <TableCell align='center'>{translate('custom.actions')}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {record?.transactions?.map((transaction) => (
                                <TableRow key={transaction._id}>
                                    <TableCell>{new Date(transaction.datetime).toLocaleString()}</TableCell>
                                    <TableCell><TransactionTypeView record={transaction} /></TableCell>
                                    <TableCell><TransactionIdView record={transaction} /></TableCell>
                                    <TableCell><AmountView currency={currency} record={transaction} /></TableCell>
                                    <TableCell><AmountRefundableView currency={currency} record={{ client_uid: record.uid, ...transaction }} /></TableCell>
                                    <TableCell><TwintRefundView record={{ client_uid: record.uid, ...transaction }} /></TableCell>
                                    <TableCell align='center'>
                                        <ShowTransactionView record={{ client_uid: record.uid, ...transaction }} />
                                        <DeleteTransactionView record={{ client_uid: record.uid, ...transaction }} />
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </ResponsiveTable>
                </Grid>
            </Grid>

            <TransactionModal 
                currency={currency}
                open={transactionModal.open}
                handleClose={() => setTransactionModal({open: false, transaction: null})}
                transactionData={transactionModal.transaction}
            />
        </>
    );
}